import type { SettingTab } from '@affine/core/modules/dialogs/constant';
import { FeatureFlagService } from '@affine/core/modules/feature-flag';
import { useI18n } from '@affine/i18n';
import {
  AccountIcon,
  AppearanceIcon,
  // STREAMLAIN: ExperimentIcon removed — experimental-features tab hidden
  EmailIcon,
  HelpCenterIcon,
  ImageIcon,
  InformationIcon,
  KeyboardIcon,
  NotificationIcon,
  PenIcon,
  // sl-canvas2-8.31: AI Confirmations icon
  SettingsIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useServices } from '@toeverything/infra';
import { useMemo } from 'react';

import { AuthService } from '../../../../modules/cloud';
import { MailSettings } from '../../../../modules/streamlain/mail/mail-settings';
import { NotificationSettings } from '../account-setting/notifications';
import type { SettingSidebarItem } from '../types';
import { AboutAffine } from './about';
// sl-y41p.16: AI Settings Tab
import { AISettings } from './ai';
import { AppearanceSettings } from './appearance';
import { EditorSettings } from './editor';
import { ExperimentalFeatures } from './experimental-features';
import { PdfSignatureSettingsSection } from './pdf-signatures';
import { Shortcuts } from './shortcuts';
import { TutorialsSettings } from './tutorials';

export type GeneralSettingList = SettingSidebarItem[];

export const useGeneralSettingList = (): GeneralSettingList => {
  const t = useI18n();
  const { authService, featureFlagService } = useServices({
    AuthService,
    FeatureFlagService,
  });
  const status = useLiveData(authService.session.status$);
  const loggedIn = status === 'authenticated';
  const enableEditorSettings = useLiveData(
    featureFlagService.flags.enable_editor_settings.$
  );

  return useMemo(() => {
    // Order: Account → Appearance → Editor → AI → Notifications → Shortcuts → Tutorials
    const settings: GeneralSettingList = [];

    if (loggedIn) {
      settings.push({
        key: 'account',
        title: t['com.streamlain.setting.account'](),
        icon: <AccountIcon />,
        testId: 'account-panel-trigger',
      });
    }

    settings.push({
      key: 'appearance',
      title: t['com.streamlain.settings.appearance'](),
      icon: <AppearanceIcon />,
      testId: 'appearance-panel-trigger',
    });

    if (enableEditorSettings) {
      settings.push({
        key: 'editor',
        title: t['com.streamlain.settings.editorSettings'](),
        icon: <PenIcon />,
        testId: 'editor-panel-trigger',
      });
    }

    if (loggedIn) {
      settings.push({
        key: 'ai',
        title: t['com.streamlain.ai-settings.title']?.() ?? 'AI Settings',
        icon: <SettingsIcon />,
        testId: 'ai-settings-panel-trigger',
      });
      settings.push({
        key: 'notifications',
        title: t['com.streamlain.setting.notifications'](),
        icon: <NotificationIcon />,
        testId: 'notifications-panel-trigger',
      });
      settings.push({
        key: 'mail',
        title: t['com.streamlain.rootAppSidebar.email'](),
        icon: <EmailIcon />,
        testId: 'mail-settings-panel-trigger',
      });
      settings.push({
        key: 'pdf-signatures',
        title: t['com.streamlain.pdf.signature.settings.title'](),
        icon: <ImageIcon />,
        testId: 'pdf-signatures-panel-trigger',
      });
    }

    settings.push({
      key: 'shortcuts',
      title: t['com.streamlain.keyboardShortcuts.title'](),
      icon: <KeyboardIcon />,
      testId: 'shortcuts-panel-trigger',
    });

    settings.push({
      key: 'tutorials',
      title: t['com.streamlain.settings.tutorials.title']?.() ?? 'Tutorial',
      icon: <HelpCenterIcon />,
      testId: 'tutorials-panel-trigger',
    });

    settings.push({
      key: 'about',
      title: t['com.streamlain.aboutAFFiNE.title'](),
      icon: <InformationIcon />,
      testId: 'about-panel-trigger',
    });

    // STREAMLAIN: experimental-features tab hidden — not relevant for our users
    // STREAMLAIN: Desktop-only meetings and backup/archived-workspaces tabs hidden.
    return settings;
  }, [t, loggedIn, enableEditorSettings]);
};

interface GeneralSettingProps {
  activeTab: SettingTab;
  mailAccountId?: string;
}

export const GeneralSetting = ({
  activeTab,
  mailAccountId,
}: GeneralSettingProps) => {
  switch (activeTab) {
    case 'shortcuts':
      return <Shortcuts />;
    case 'notifications':
      return <NotificationSettings />;
    case 'mail':
      return <MailSettings initialAccountId={mailAccountId} />;
    case 'pdf-signatures':
      return <PdfSignatureSettingsSection />;
    case 'editor':
      return <EditorSettings />;
    case 'canvas-edges':
      // Canvas edges settings are now part of the Editor tab
      return <EditorSettings />;
    case 'appearance':
      return <AppearanceSettings />;
    case 'ai-confirmations':
    case 'ai-personalization':
    case 'ai':
      return <AISettings />;
    case 'experimental-features':
      return <ExperimentalFeatures />;
    case 'tutorials':
      return <TutorialsSettings />;
    case 'about':
      return <AboutAffine />;
    default:
      return null;
  }
};
